import {
  EntityIdentifier,
  FlowMeterType,
  FreightTypeEntity,
  getObjectId,
  MeterType,
  toFlowMeterType,
  toMeterType
} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Modal, ModalProps, Switch, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {UnitOfMeasureSelect} from "../../select/entity/UnitOfMeasureSelect/UnitOfMeasureSelect";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {MeterTypeSelect} from "../../select/entity/MeterTypeSelect/MeterTypeSelect";
import {FlowMeterTypeSelect} from "../../select/entity/FlowMeterTypeSelect/FlowMeterTypeSelect";
import {EntityTypeSelect} from "../../select/entity/EntityTypeSelect/EntityTypeSelect";
import {EntitySelect} from "../../select/entity/EntitySelect/EntitySelect";

interface MeterFormProps {
  options?: MeterFormOptions;
  defaultValues?: Partial<MeterFormValues>;
  onSubmit?: (values: MeterFormValues) => void;
  submitting?: boolean;
}

export interface MeterFormOptions {
  // props here
}

export interface MeterFormValues {
  name: string
  type: MeterType
  flowMeterType?: FlowMeterType
  barcode?: string
  unitOfMeasure: string
  freightType?: FreightTypeEntity | string;
  object?: EntityIdentifier
  doNotUse?: boolean
}

const getDefaultValues = (defaultValues: Partial<MeterFormValues> | undefined) => {
  return {
    name: defaultValues?.name,
    type: defaultValues?.type?.code,
    flowMeterType: defaultValues?.flowMeterType?.code,
    barcode: defaultValues?.barcode,
    unitOfMeasure: defaultValues?.unitOfMeasure,
    freightType: getObjectId(defaultValues?.freightType),
    object: {
      id: defaultValues?.object?.id,
      type: defaultValues?.object?.type,
    },
    doNotUse: defaultValues?.doNotUse,
  }
}

export const MeterForm = (props: MeterFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {regionId} = useApp();

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      let object: EntityIdentifier | undefined = undefined
      if (values.object.id && values.object.type) {
        object = {
          id: values.object.id,
          type: values.object.type,
        }
      }

      onSubmit({
        name: values.name!,
        type: toMeterType(values.type!),
        flowMeterType: values?.flowMeterType ? toFlowMeterType(values.flowMeterType) : undefined,
        barcode: values.barcode,
        unitOfMeasure: values.unitOfMeasure!,
        freightType: values.freightType,
        object: object,
        doNotUse: values.doNotUse,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <TextInput
        label={t("common.entity.meter.form.field.name", "Name")}
        placeholder={t<string>("common.entity.meter.form.field.name.placeholder", "Name")}
        styles={{wrapper: {width: "50%"}}}
        withAsterisk
        required
        {...form.getInputProps("name")} />
      <Divider my="sm" variant="dotted"/>
      <MeterTypeSelect
        label={t("common.entity.meter.form.field.type", "Type")}
        placeholder={t<string>("common.entity.meter.form.field.type.placeholder", "Type")}
        styles={{wrapper: {width: "50%"}}}
        clearable
        withAsterisk
        required
        {...form.getInputProps("type")} />
      <FlowMeterTypeSelect
        label={t("common.entity.meter.form.field.flowmetertype", "Flow Meter Type")}
        placeholder={t<string>("common.entity.meter.form.field.type.flowmetertype", "Flow Meter Type")}
        styles={{wrapper: {width: "50%"}}}
        clearable
        {...form.getInputProps("flowMeterType")} />
      <Divider my="sm" variant="dotted"/>
      <TextInput
        label={t("common.entity.meter.form.field.barcode", "QR Code")}
        placeholder={t<string>("common.entity.meter.form.field.barcode.placeholder", "QR Code")}
        styles={{wrapper: {width: "50%"}}}
        {...form.getInputProps("barcode")} />
      <Divider my="sm" variant="dotted"/>
      <UnitOfMeasureSelect
        label={t("common.entity.meter.form.field.uom", "Unit Of Measure")}
        placeholder={t<string>("common.entity.meter.form.field.uom.placeholder", "Unit Of Measure")}
        clearable
        withAsterisk
        required
        {...form.getInputProps("unitOfMeasure")} />
      <FreightTypeSelect
        label={t("common.entity.meter.form.field.freighttype", "Freight Type")}
        placeholder={t<string>("common.entity.meter.form.field.freighttype.placeholder", "Freight Type")}
        selector={{
          regionId: regionId,
        }}
        {...form.getInputProps("freightType")} />
      <Divider my="sm" variant="dotted"/>
      <EntityTypeSelect
        options={['SITE', 'WELL', 'CONTAINER']}
        label={t("common.entity.meter.form.field.object", "Object")}
        placeholder={t<string>("common.entity.meter.form.field.object.placeholder", "Object")}
        clearable
        {...form.getInputProps("object.type")} />
      <EntitySelect
        pt="md"
        entityType={form.getTransformedValues().object?.type}
        regionId={regionId}
        clearable
        {...form.getInputProps("object.id")} />
      <Divider my="sm" variant="dotted"/>
      <Switch
        label={t("common.entity.meter.form.field.donotuse", "Do Not Use")}
        placeholder={t<string>("common.entity.meter.form.field.donotuse", "Do Not Use")}
        {...form.getInputProps("doNotUse", {type: 'checkbox'})} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface MeterFormModalProps extends MeterFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const MeterFormModal = (props: MeterFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <MeterForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
